import { SailingDayBundle, SimpleSailingDayBundle } from '../../shared/types/sailing';
import { MergedVariables, ReplacedDict, TranslatedReplacedDict } from '../types/mergedVars';
import { mapBy } from './functional';
import { captureBetweenDollars } from './utils';

export function generateReplaceDict<T extends Partial<MergedVariables>>(
  mergedVars: string[],
  container?: T
) {
  const replaceDict: ReplacedDict = {};

  mergedVars.forEach(mv => {
    replaceDict[mv] = {
      enabled: (container?.replacements || {})[mv]?.enabled ?? false,
      value: (container?.replacements || {})[mv]?.value ?? '',
    };
  });

  return replaceDict;
}

export function generateTranslDict<T extends Partial<MergedVariables>>(
  mergedVars: string[],
  languages: string[],
  container?: T
) {
  const translReplaceDict: TranslatedReplacedDict = {};

  languages.forEach(lang => {
    translReplaceDict[lang] = {};

    mergedVars.forEach(mv => {
      translReplaceDict[lang][mv] = {
        enabled: ((container?.translations ?? {})[lang] || {})[mv]?.enabled ?? false,
        value: ((container?.translations ?? {})[lang] || {})[mv]?.value ?? '',
      };
    });
  });

  return translReplaceDict;
}

export function evaluateMergedVars<T extends Partial<MergedVariables>>(
  content: string,
  dayBundle: SimpleSailingDayBundle | SailingDayBundle | undefined,
  languages: string[],
  selectedLanguage: string | undefined,
  defaultLanguage: string,
  container?: T,
  decoratorFn?: (text: string, key?: string, isMissing?: boolean) => string
): [string, string[]] {
  const mergedVars = captureBetweenDollars(content || '', true);
  const mergedVarKeys = mergedVars.map(m => m[1]);

  const replacements = generateReplaceDict(mergedVarKeys, container);
  const translations = generateTranslDict(mergedVarKeys, languages, container);

  let workingVal = content;

  mergedVars.forEach(mv => {
    const mAll = mv[0];
    const mKey = mv[1];
    const mDef = mv[2];

    const tokens = mAll.split('|');
    const mDefEmpty = tokens.length === 2 && tokens[1] === '$';
    const decorator = decoratorFn || ((text: string) => text);

    if (mKey.startsWith('GO_')) {
      // this is a day context variable
      const [year, month, day] = dayBundle?.dateIso.split('-') ?? [undefined, undefined, undefined];
      const nextSailingDay = dayBundle?.sailing.days[dayBundle.dayIndex + 1];

      const monthName = dayBundle?.day.date.toLocaleDateString(
        selectedLanguage || defaultLanguage,
        {
          month: 'long',
        }
      );

      const weekdayName = dayBundle?.day.date.toLocaleDateString(
        selectedLanguage || defaultLanguage,
        {
          weekday: 'long',
        }
      );

      switch (mKey) {
        case 'GO_PROPERTY_NAME': {
          let replacement = dayBundle?.sailing.property.name;
          if (replacement) {
            workingVal = workingVal.replace(`${mAll}`, replacement);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_NAME': {
          let replacement = dayBundle?.day.name;
          if (replacement) {
            workingVal = workingVal.replace(`${mAll}`, replacement);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_THEME': {
          const dayThemes = mapBy(dayBundle?.sailing.property.brand.dayThemes || [], '_id');
          let replacement = dayThemes[dayBundle?.day.dayTheme ?? ''];
          if (replacement) {
            workingVal = workingVal.replace(`${mAll}`, replacement.name);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_LOCATIONS_NAME': {
          let replacement = dayBundle?.day.locations.map(l => l.name).join(', ');
          if (replacement) {
            workingVal = workingVal.replace(`${mAll}`, replacement);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_NEXT_DAY_LOCATIONS_NAME': {
          let replacement = nextSailingDay?.locations.map(l => l.name).join(', ');
          if (replacement) {
            workingVal = workingVal.replace(`${mAll}`, replacement);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_DAY': {
          if (day) {
            workingVal = workingVal.replace(`${mAll}`, day);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_DAYNAME': {
          if (weekdayName) {
            workingVal = workingVal.replace(`${mAll}`, weekdayName);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_YEAR': {
          if (year) {
            workingVal = workingVal.replace(`${mAll}`, year);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_MONTH': {
          if (month) {
            workingVal = workingVal.replace(`${mAll}`, month);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
        case 'GO_DAY_MONTHNAME': {
          if (monthName) {
            workingVal = workingVal.replace(`${mAll}`, monthName);
          } else if (mDef) {
            workingVal = workingVal.replace(`${mAll}`, mDef);
          } else if (mDefEmpty) {
            workingVal = workingVal.replace(`${mAll}`, '');
          }
          break;
        }
      }
    } else if (selectedLanguage && selectedLanguage !== defaultLanguage) {
      // needs translated replacements or normal replacements
      if (translations[selectedLanguage][mKey] && translations[selectedLanguage][mKey].enabled) {
        workingVal = workingVal.replace(
          `${mAll}`,
          decorator(translations[selectedLanguage][mKey].value, mKey)
        );
      } else if (replacements[mKey] && replacements[mKey].enabled) {
        workingVal = workingVal.replace(`${mAll}`, decorator(replacements[mKey].value, mKey));
      } else if (mDef) {
        workingVal = workingVal.replace(`${mAll}`, decorator(mDef, mKey));
      } else if (mDefEmpty) {
        workingVal = workingVal.replace(`${mAll}`, decorator('', mKey));
      } else {
        workingVal = workingVal.replace(`${mAll}`, decorator(`${mAll}`, mKey, true));
      }
    } else {
      // needs normal replacements
      if (replacements[mKey] && replacements[mKey].enabled) {
        workingVal = workingVal.replace(`${mAll}`, decorator(replacements[mKey].value, mKey));
      } else if (mDef) {
        workingVal = workingVal.replace(`${mAll}`, decorator(mDef, mKey));
      } else if (mDefEmpty) {
        workingVal = workingVal.replace(`${mAll}`, decorator('', mKey));
      } else {
        workingVal = workingVal.replace(`${mAll}`, decorator(`${mAll}`, mKey, true));
      }
    }
  });

  return [workingVal, mergedVarKeys];
}

export function hasAllMergedVarsSet<T extends Partial<MergedVariables>>(
  content: string,
  selectedLanguage: string | undefined,
  defaultLanguage: string,
  container?: T
) {
  const mergedVarKeys = captureBetweenDollars(content || '');
  if (mergedVarKeys.length < 1) return true;

  if (!container) return false;

  return mergedVarKeys
    .map(mv => {
      const mKey = mv[1];

      if (selectedLanguage && selectedLanguage !== defaultLanguage) {
        return ((container.translations || {})[selectedLanguage] || {})[mKey]?.enabled === true;
      } else {
        return (container.replacements || {})[mKey]?.enabled === true;
      }
    })
    .reduce((acc, x) => acc && x, true);
}
